import React, { useCallback, useMemo } from 'react';
import {
  BuilderChatSideFillIcon,
  BuilderChatSideIcon,
  BuilderExperiencesFill,
  BuilderExperiencesIcon,
  BuilderHomeFillIcon,
  BuilderHomeIcon,
  BuilderInsightsIcon,
  BuilderInternFillIcon,
  BuilderInternIcon,
  BuilderMegaphoneFillIcon,
  BuilderMegaphoneIcon,
  BuilderNineDotGrid,
  BuilderStoreFillIcon,
  BuilderStoreIcon,
  BuilderWalletFillIcon,
  BuilderWalletIcon,
  Button,
  Divider,
  Grid,
  RobloxIcon,
  StudioIcon,
  Typography,
  useMediaQuery,
} from '@rbx/ui';
import { Badge, Icon } from '@rbx/foundation-ui';
import { useTranslation } from '@rbx/intl';
import RailItem from './RailItem';
import { useRailContext } from '../../layout/providers/RailProvider';
import WorkplaceSelector from '../../components/WorkplaceSelector';
import { clickRailEventModel } from '../../event/eventConstants';
import { TSendEvent } from '../../providers/EventProvider';
import useProductUrls from '../../utils/useProductUrls';
import useScrollStyles from '../../hooks/useScrollStyles';
import { ProductKey, TProductKey } from '../../types';
import useNavigationConfigs from '../../hooks/useNavigationConfigs';
import { useWorkspaces, CreatorType } from '../../providers/WorkspaceProvider';
import RailHeader from './RailHeader';
import useRailStyles from './Rail.styles';

const FINANCES_PATHS = [
  '/dashboard/devex',
  '/dashboard/transactions',
  '/dashboard/account-information',
  '/dashboard/billing',
  '/dashboard/payments',
  '/dashboard/group/payouts',
];

type TPrimaryRailContentProps = {
  sendEvent: TSendEvent;
  openStudio: VoidFunction;
  login: VoidFunction;
  pathname: string;
  isAuth: boolean;
};

export const PrimaryRailContent: React.FC<TPrimaryRailContentProps> = ({
  pathname,
  isAuth,
  openStudio,
  sendEvent,
  login,
}) => {
  const {
    cx,
    classes: { railContainer, railContainerCompact, railContainerTransition, labelsRailContainer },
  } = useRailStyles();

  const {
    classes: { scroll },
  } = useScrollStyles();

  const {
    primaryRailCompact,
    drawerVariant,
    allToolsOpen,
    learnOpen,
    isReady,
    shouldAnimate,
    setLearnOpen,
    setAllToolsOpen,
    setPrimaryRailOpen,
  } = useRailContext();

  const { currentProduct, enableAdsManager, showUpdatesInNav } = useNavigationConfigs();
  const {
    currentWorkspace: { creatorType },
  } = useWorkspaces();

  const { translate } = useTranslation();
  const hideStudio = useMediaQuery((theme) => theme.breakpoints.down('Medium'));

  const { Dashboard, Store, Forum, Ads, Roblox, Documentation } = useProductUrls();

  const isOnDocsite = ['Documentation', 'Assistant'].includes(currentProduct);

  const selectItem = useCallback(
    (event: string) => {
      sendEvent(clickRailEventModel(event));
      if (drawerVariant === 'temporary' && event !== 'Learn') {
        setPrimaryRailOpen(false);
      }

      if (event === 'Learn') {
        if (isOnDocsite) {
          setLearnOpen(!learnOpen);
        }
      } else {
        setLearnOpen(false);
      }
    },
    [drawerVariant, learnOpen, sendEvent, setLearnOpen, setPrimaryRailOpen, isOnDocsite],
  );

  const active = useMemo(() => {
    if (learnOpen) {
      return 'Documentation';
    }

    if (pathname.startsWith(Dashboard.updates)) {
      return ProductKey.Updates;
    }

    if (currentProduct === ProductKey.CreatorDashboard) {
      if (pathname.startsWith(Dashboard.creations)) {
        return 'Creations';
      }

      if (pathname.startsWith(Dashboard.analytics)) {
        return 'Analytics';
      }

      if (pathname.startsWith(Dashboard.collaborations)) {
        return 'Collaboration';
      }

      if (FINANCES_PATHS.includes(pathname)) {
        return 'Finances';
      }
    }

    const productPaths: TProductKey[] = [
      ProductKey.Home,
      ProductKey.Documentation,
      ProductKey.Store,
      ProductKey.Forum,
      ProductKey.Advertise,
    ];

    if (enableAdsManager) {
      productPaths.push(ProductKey.Advertise);
    }

    if (showUpdatesInNav) {
      productPaths.push(ProductKey.Updates);
    }

    if (productPaths.includes(currentProduct)) {
      return currentProduct;
    }

    return null;
  }, [
    learnOpen,
    currentProduct,
    enableAdsManager,
    showUpdatesInNav,
    pathname,
    Dashboard.creations,
    Dashboard.analytics,
    Dashboard.collaborations,
    Dashboard.updates,
  ]);

  return (
    <Grid
      classes={{
        root: cx(scroll, railContainer, {
          [railContainerTransition]: isReady,
          [railContainerCompact]: primaryRailCompact,
          [labelsRailContainer]: primaryRailCompact,
        }),
      }}>
      <RailHeader
        compact={primaryRailCompact}
        icon={<RobloxIcon />}
        label={translate('Label.Creator')}
        onClick={() => selectItem('Header')}
        href={Dashboard.home}
      />
      {isAuth && <WorkplaceSelector collapsed={primaryRailCompact} />}
      <RailItem
        enableAnimation={isReady && shouldAnimate}
        compact={primaryRailCompact}
        icon={<BuilderHomeIcon />}
        active={active === 'Home'}
        activeIcon={<BuilderHomeFillIcon />}
        label={translate('Heading.Home')}
        onClick={() => selectItem('Home')}
        href={Dashboard.home}
      />
      {isAuth && (
        <RailItem
          enableAnimation={isReady && shouldAnimate}
          compact={primaryRailCompact}
          icon={<BuilderExperiencesIcon />}
          activeIcon={<BuilderExperiencesFill />}
          active={active === 'Creations'}
          label={translate('Heading.Creations')}
          onClick={() => selectItem('Creations')}
          href={Dashboard.creations}
        />
      )}
      <RailItem
        enableAnimation={isReady && shouldAnimate}
        compact={primaryRailCompact}
        icon={<BuilderInternIcon />}
        activeIcon={<BuilderInternFillIcon />}
        active={active === 'Documentation'}
        label={translate('Heading.Learn')}
        onClick={() => {
          selectItem('Learn');
        }}
        href={
          !isOnDocsite
            ? `${Documentation.home.replace(/\/$/, '')}?navFromCreatorHub=true`
            : undefined
        } // if we're on docsite, use the menu to navigate (href is undefined)
      />
      <RailItem
        enableAnimation={isReady && shouldAnimate}
        compact={primaryRailCompact}
        icon={<BuilderStoreIcon />}
        activeIcon={<BuilderStoreFillIcon />}
        active={active === 'Store'}
        label={translate('Heading.Store')}
        onClick={() => selectItem('Store')}
        href={Store.home}
      />
      <RailItem
        enableAnimation={isReady && shouldAnimate}
        compact={primaryRailCompact}
        icon={<BuilderChatSideIcon />}
        activeIcon={<BuilderChatSideFillIcon />}
        active={active === 'Forum'}
        label={translate('Heading.Forums')}
        onClick={() => selectItem('Forum')}
        href={Forum.home}
      />
      {showUpdatesInNav && (
        <RailItem
          enableAnimation={isReady && shouldAnimate}
          compact={primaryRailCompact}
          icon={<Icon name='icon-regular-curved-rectangle-megaphone' size='Medium' />}
          activeIcon={<Icon name='icon-filled-curved-rectangle-megaphone' size='Medium' />}
          active={active === ProductKey.Updates}
          label={translate('Heading.Updates')}
          adornment={
            Date.now() < new Date('2026-05-05').getTime() ? (
              <Badge label={translate('Label.New')} />
            ) : undefined
          }
          onClick={() => selectItem(ProductKey.Updates)}
          href={Dashboard.updates}
        />
      )}
      {isAuth && (
        <React.Fragment>
          {!primaryRailCompact && <Divider flexItem />}
          <RailItem
            enableAnimation={isReady && shouldAnimate}
            compact={primaryRailCompact}
            icon={<BuilderWalletIcon />}
            activeIcon={<BuilderWalletFillIcon />}
            active={active === 'Finances'}
            label={translate('Heading.Finances')}
            onClick={() => selectItem('Finances')}
            href={Dashboard.finances}
          />
          <RailItem
            enableAnimation={isReady && shouldAnimate}
            compact={primaryRailCompact}
            icon={<BuilderInsightsIcon />}
            active={active === 'Analytics'}
            label={translate('Title.Analytics')}
            onClick={() => selectItem('Analytics')}
            href={Dashboard.analytics}
          />
          {creatorType === CreatorType.Group && (
            <RailItem
              enableAnimation={isReady && shouldAnimate}
              compact={primaryRailCompact}
              icon={<Icon name='icon-regular-two-people' />}
              activeIcon={<Icon name='icon-filled-two-people' />}
              active={active === 'Collaboration'}
              label={translate('Heading.Collaboration')}
              onClick={() => selectItem('Collaboration')}
              href={Dashboard.groupProfile}
            />
          )}
          <RailItem
            enableAnimation={isReady && shouldAnimate}
            compact={primaryRailCompact}
            active={active === ProductKey.Advertise}
            icon={<BuilderMegaphoneIcon />}
            activeIcon={<BuilderMegaphoneFillIcon />}
            label={translate('Heading.Ads')}
            onClick={() => selectItem('Ads')}
            href={Ads.home}
          />
          {!primaryRailCompact && <Divider flexItem />}
        </React.Fragment>
      )}
      {isAuth || primaryRailCompact ? (
        <RailItem
          enableAnimation={isReady && shouldAnimate}
          compact={primaryRailCompact}
          icon={<BuilderNineDotGrid />}
          label={translate('Heading.AllTools')}
          onClick={() => {
            if (isAuth) {
              sendEvent(clickRailEventModel('AllTools'));
              setAllToolsOpen(!allToolsOpen);
            } else {
              login();
              sendEvent(clickRailEventModel('AllToolsLogin'));
            }
          }}
        />
      ) : (
        <Button
          variant='contained'
          color='secondary'
          onClick={() => {
            sendEvent(clickRailEventModel('AllToolsLogin'));
            login();
          }}>
          <Typography variant='smallLabel2' noWrap>
            {translate('Action.LogInAllTools')}
          </Typography>
        </Button>
      )}
      <RailItem
        enableAnimation={isReady && shouldAnimate}
        bottom
        compact={primaryRailCompact}
        icon={<RobloxIcon />}
        label={translate('Label.RobloxWebsite')}
        onClick={() => selectItem('RobloxWebsite')}
        href={Roblox.home}
      />
      {!hideStudio && (
        <RailItem
          enableAnimation={isReady && shouldAnimate}
          compact={primaryRailCompact}
          icon={<StudioIcon />}
          label={translate('Heading.Studio')}
          onClick={() => {
            selectItem('studio');
            openStudio();
          }}
        />
      )}
    </Grid>
  );
};

export default PrimaryRailContent;
